import { Link } from 'react-router-dom';
import { MockExam2Question } from '../data/mockExam2Questions';

interface Exam2ResultsProps {
  questions: MockExam2Question[];
  userAnswers: (string | null)[];
  timeUsed: number;
  totalTime: number;
  onReview: () => void;
  onRetake: () => void;
}

const DOMAIN_COLORS: Record<string, { bar: string; bg: string; text: string }> = {
  'Algebra': { bar: 'bg-violet-500', bg: 'bg-violet-50', text: 'text-violet-700' },
  'Advanced Math': { bar: 'bg-rose-500', bg: 'bg-rose-50', text: 'text-rose-700' },
  'Problem-Solving & Data Analysis': { bar: 'bg-orange-500', bg: 'bg-orange-50', text: 'text-orange-700' },
  'Geometry & Trigonometry': { bar: 'bg-sky-500', bg: 'bg-sky-50', text: 'text-sky-700' },
};

export default function Exam2Results({
  questions,
  userAnswers,
  timeUsed,
  totalTime,
  onReview,
  onRetake,
}: Exam2ResultsProps) {
  const correct = userAnswers.filter((a, i) => a === questions[i].correctAnswer).length;
  const total = questions.length;
  const pct = Math.round((correct / total) * 100);
  const unanswered = userAnswers.filter((a) => a === null).length;
  const minutesUsed = Math.floor(timeUsed / 60);
  const secondsUsed = timeUsed % 60;

  const getGrade = () => {
    if (pct >= 90) return { label: 'Outstanding!', emoji: '🏆', color: 'text-emerald-600' };
    if (pct >= 80) return { label: 'Excellent!', emoji: '🌟', color: 'text-emerald-600' };
    if (pct >= 70) return { label: 'Good Job!', emoji: '👍', color: 'text-teal-600' };
    if (pct >= 60) return { label: 'Keep Practicing!', emoji: '💪', color: 'text-amber-600' };
    return { label: 'Review & Retry', emoji: '📚', color: 'text-red-500' };
  };

  const grade = getGrade();

  const domains = ['Algebra', 'Advanced Math', 'Problem-Solving & Data Analysis', 'Geometry & Trigonometry'] as const;
  const domainStats = domains.map((domain) => {
    const domainQs = questions.map((q, i) => ({ q, i })).filter(({ q }) => q.domain === domain);
    const domainCorrect = domainQs.filter(({ q, i }) => userAnswers[i] === q.correctAnswer).length;
    return { domain, correct: domainCorrect, total: domainQs.length };
  });

  const subtopicMap: Record<string, { correct: number; total: number }> = {};
  questions.forEach((q, i) => {
    if (!subtopicMap[q.subtopic]) subtopicMap[q.subtopic] = { correct: 0, total: 0 };
    subtopicMap[q.subtopic].total++;
    if (userAnswers[i] === q.correctAnswer) subtopicMap[q.subtopic].correct++;
  });
  const weakAreas = Object.entries(subtopicMap)
    .filter(([, v]) => v.correct / v.total < 0.6)
    .sort((a, b) => a[1].correct / a[1].total - b[1].correct / b[1].total)
    .slice(0, 4);

  return (
    <div className="max-w-5xl mx-auto px-4 py-10">
      <Link to="/sat-prep" className="inline-flex items-center gap-2 text-violet-600 hover:text-violet-700 font-semibold mb-8 transition-colors">
        <i className="ri-arrow-left-line" />
        Back to SAT Prep
      </Link>

      <div className="bg-gradient-to-br from-violet-500 to-rose-500 rounded-3xl p-10 text-white text-center mb-8 shadow-xl">
        <div className="text-6xl mb-3">{grade.emoji}</div>
        <h1 className="text-4xl font-bold mb-1">{grade.label}</h1>
        <p className="text-violet-100 mb-8">SAT Math Mock Exam 2 Complete</p>
        <div className="text-8xl font-black mb-2">{pct}%</div>
        <p className="text-violet-100 text-lg">Overall Score</p>

        <div className="grid grid-cols-4 gap-4 mt-8">
          {[
            { label: 'Correct', value: correct, icon: 'ri-check-line' },
            { label: 'Incorrect', value: total - correct - unanswered, icon: 'ri-close-line' },
            { label: 'Unanswered', value: unanswered, icon: 'ri-question-line' },
            { label: 'Time Used', value: `${minutesUsed}m ${String(secondsUsed).padStart(2, '0')}s`, icon: 'ri-time-line' },
          ].map((stat) => (
            <div key={stat.label} className="bg-white/20 rounded-2xl p-4 backdrop-blur-sm">
              <div className="w-8 h-8 flex items-center justify-center mx-auto mb-2">
                <i className={`${stat.icon} text-xl`} />
              </div>
              <div className="text-2xl font-bold">{stat.value}</div>
              <div className="text-xs text-violet-100 mt-1">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>

      <Link
        to="/sat-prep/study-plan"
        className="group block bg-gradient-to-r from-violet-600 to-rose-500 rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 overflow-hidden mb-6"
      >
        <div className="p-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-5">
            <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center flex-shrink-0">
              <i className="ri-road-map-line text-white text-3xl" />
            </div>
            <div className="text-white">
              <h3 className="text-xl font-black mb-1">Get Your Personalized Study Plan</h3>
              <p className="text-violet-100 text-sm max-w-lg">
                Based on your results, we'll rank your weak domains and build a targeted weekly schedule.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 bg-white text-violet-700 font-black px-7 py-3 rounded-xl group-hover:bg-violet-50 transition-colors whitespace-nowrap flex-shrink-0">
            View Study Plan
            <i className="ri-arrow-right-line text-lg group-hover:translate-x-1 transition-transform" />
          </div>
        </div>
      </Link>

      <div className="grid lg:grid-cols-2 gap-6 mb-6">
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-5 flex items-center gap-2">
            <i className="ri-bar-chart-2-line text-violet-600 w-6 h-6 flex items-center justify-center" />
            Score by Domain
          </h2>
          <div className="space-y-5">
            {domainStats.map(({ domain, correct: dc, total: dt }) => {
              const dpct = Math.round((dc / dt) * 100);
              const colors = DOMAIN_COLORS[domain];
              return (
                <div key={domain}>
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-sm font-semibold text-gray-700 truncate max-w-[200px]">{domain}</span>
                    <span className={`text-sm font-bold ${colors.text}`}>{dc}/{dt} ({dpct}%)</span>
                  </div>
                  <div className="w-full bg-gray-100 rounded-full h-3 overflow-hidden">
                    <div className={`h-full rounded-full transition-all duration-700 ${colors.bar}`} style={{ width: `${dpct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-lg p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-5 flex items-center gap-2">
            <i className="ri-focus-3-line text-amber-500 w-6 h-6 flex items-center justify-center" />
            Areas to Review
          </h2>
          {weakAreas.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-32 text-center">
              <div className="text-4xl mb-2">🎉</div>
              <p className="text-gray-600 font-medium">No weak areas — excellent performance!</p>
            </div>
          ) : (
            <div className="space-y-4">
              {weakAreas.map(([subtopic, stats]) => {
                const spct = Math.round((stats.correct / stats.total) * 100);
                return (
                  <div key={subtopic} className="flex items-center gap-4 p-3 bg-red-50 rounded-xl border border-red-100">
                    <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <i className="ri-alert-line text-red-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-gray-800 text-sm truncate">{subtopic}</p>
                      <p className="text-xs text-red-500">{stats.correct}/{stats.total} correct ({spct}%)</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          <div className="mt-6 p-4 bg-violet-50 rounded-xl border border-violet-100">
            <p className="text-sm font-semibold text-gray-700 mb-1 flex items-center gap-2">
              <i className="ri-time-line text-violet-600 w-4 h-4 flex items-center justify-center" />
              Time Analysis
            </p>
            <p className="text-xs text-gray-600">
              You used <strong>{minutesUsed}m {String(secondsUsed).padStart(2, '0')}s</strong> out of 70 minutes.
              That's about <strong>{Math.round(timeUsed / total)}s per question</strong> on average.
              {timeUsed / total < 90 ? ' Great pacing!' : ' Try to move faster on easier questions.'}
            </p>
          </div>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-4">
        <button onClick={onReview} className="flex-1 bg-violet-600 hover:bg-violet-700 text-white font-bold py-4 rounded-xl transition-colors flex items-center justify-center gap-2 whitespace-nowrap">
          <i className="ri-file-list-3-line text-xl" />
          Review All Answers
        </button>
        <button onClick={onRetake} className="flex-1 bg-white hover:bg-gray-50 text-gray-800 font-bold py-4 rounded-xl border-2 border-gray-200 transition-colors flex items-center justify-center gap-2 whitespace-nowrap">
          <i className="ri-refresh-line text-xl" />
          Retake Exam
        </button>
        <Link to="/sat-prep/study-plan" className="flex-1 bg-rose-500 hover:bg-rose-600 text-white font-bold py-4 rounded-xl transition-colors flex items-center justify-center gap-2 whitespace-nowrap">
          <i className="ri-road-map-line text-xl" />
          My Study Plan
        </Link>
        <Link to="/sat-prep" className="flex-1 bg-gray-800 hover:bg-gray-900 text-white font-bold py-4 rounded-xl transition-colors flex items-center justify-center gap-2 whitespace-nowrap">
          <i className="ri-arrow-left-line text-xl" />
          Back to SAT Prep
        </Link>
      </div>
    </div>
  );
}
